import { useMemo } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Button } from '@flintmint/web-shared/components/button';

import type { NoDataProps } from '@flintmint/web-shared/components/no-data';

export const useWorkspaceNoData = (props: UseNoDataProps) => {
  const {
    icon,
    clearSearch,
    resetFilters,
    descriptions,
    defaultActions = [],
    hasFilters = false,
    hasSearchValue = false,
    defaultActionRenderer,
  } = props;

  const renderDefaults = () => {
    return (
      <>
        {defaultActionRenderer?.()}
        {defaultActions.map(({ label, icon, ...buttonProps }, idx) => (
          <Button key={`default-${idx}`} icon={<Icon type={icon!} size={20} />} data-testid="new-item-btn" {...buttonProps}>
            {label}
          </Button>
        ))}
      </>
    );
  };

  const noDataProps: Pick<NoDataProps, 'description' | 'actionRenderer'> & {
    iconType: string;
  } = useMemo(() => {
    const { noData, searchNoFilters, searchHasFilters } = descriptions;

    if (hasSearchValue || hasFilters) {
      return {
        iconType: 'search-01-bulk',
        description: hasFilters ? searchHasFilters ?? searchNoFilters : searchNoFilters,
        actionRenderer: () => (
          <>
            {renderDefaults()}
            {hasFilters ? (
              <Button color="light" onClick={resetFilters}>
                Reset Filters
              </Button>
            ) : (
              <Button color="light" onClick={clearSearch}>
                Clear Search
              </Button>
            )}
          </>
        ),
      };
    }

    return {
      iconType: icon,
      description: noData,
      actionRenderer: () => (
        <>
          {renderDefaults()}
          <Button color="light" disabled>
            Guide me!
          </Button>
        </>
      ),
    };
  }, [icon, descriptions, hasFilters, hasSearchValue, clearSearch, resetFilters, defaultActions, defaultActionRenderer]);

  return noDataProps;
};

interface UseNoDataProps {
  icon: string;
  hasFilters?: boolean;
  hasSearchValue?: boolean;
  clearSearch: VoidFunction;
  resetFilters?: VoidFunction;
  descriptions: Record<string, string>;
  defaultActions?: NoDataProps['actions'];
  defaultActionRenderer?: NoDataProps['actionRenderer'];
}
